import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from './AuthContext'
import { API_BASE_URL } from '../config/api'

const GameContext = createContext()

export function useGame() {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within a GameProvider')
  }
  return context
}

// --- Internal Hook for API Handling ---
function useApi(session) {
    const pendingRequests = useRef(new Map())

    const request = useCallback(async (endpoint, options = {}) => {
        const method = options.method || 'GET'
        const bodyStr = options.body ? JSON.stringify(options.body) : ''
        const requestKey = `${method}:${endpoint}:${bodyStr}`

        // Deduping for GET requests
        if (method === 'GET' && pendingRequests.current.has(requestKey)) {
            return pendingRequests.current.get(requestKey)
        }

        const execute = async () => {
            const url = `${API_BASE_URL}/api/v1${endpoint}`
            const headers = {
                'Content-Type': 'application/json',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
            }

            const config = {
                ...options,
                headers: { ...headers, ...options.headers },
                body: options.body ? JSON.stringify(options.body) : undefined
            }

            const response = await fetch(url, config)
            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || `HTTP error! status: ${response.status}`)
            }
            return data
        }

        const promise = execute().finally(() => {
            if (method === 'GET') pendingRequests.current.delete(requestKey)
        })

        if (method === 'GET') {
            pendingRequests.current.set(requestKey, promise)
        }

        return promise
    }, [session])

    return request
}

export function GameProvider({ children }) {
  const { session } = useAuth()
  const api = useApi(session)
  
  const [games, setGames] = useState([])
  const [currentGame, setCurrentGame] = useState(null)
  const [availableGames, setAvailableGames] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // --- Helper to wrap API calls with state ---
  const withState = useCallback(async (fn, shouldSetLoading = true) => {
      if (shouldSetLoading) setLoading(true)
      setError(null)
      try {
          return await fn()
      } catch (err) {
          setError(err.message)
          throw err
      } finally {
          if (shouldSetLoading) setLoading(false)
      }
  }, [])

  // --- Game Actions ---

  const createGame = useCallback((config = {}) => {
      return withState(async () => {
          const game = await api('/games', { method: 'POST', body: config })
          setCurrentGame(game)
          setGames(prev => [game, ...prev])
          return game
      })
  }, [api, withState])

  const joinGame = useCallback((gameId) => {
      return withState(async () => {
          const game = await api(`/games/${gameId}/join`, { method: 'PUT' })
          setCurrentGame(game)
          setGames(prev => {
            const exists = prev.find(g => g.id === game.id)
            if (exists) {
                return prev.map(g => g.id === game.id ? game : g)
            }
            return [game, ...prev]
          })
          return game
      })
  }, [api, withState])

  // Removed dead code: makeMove (WebSocket should be used for moves)

  const getGame = useCallback((gameId) => {
      return withState(async () => {
          return await api(`/games/${gameId}`)
      }, true) // Loading true
  }, [api, withState])

  const listGames = useCallback(() => {
      return withState(async () => {
          const response = await api('/games')
          setGames(response.games || [])
          return response.games
      })
  }, [api, withState])

  const listAvailableGames = useCallback(() => {
      // Don't trigger global loading for background refresh? 
      // Existing code did set loading. stick to it for now.
      return withState(async () => {
          const response = await api('/games/available')
          setAvailableGames(response.games || [])
          return response.games
      })
  }, [api, withState])

  const resignGame = useCallback((gameId) => {
      return withState(async () => {
          const response = await api(`/games/${gameId}/resign`, { method: 'PUT' })
          if (currentGame?.id === gameId) setCurrentGame(response.game)
          return response
      }, false) // No global loading for actions usually
  }, [api, withState, currentGame?.id])

  const deleteGame = useCallback((gameId) => {
      return withState(async () => {
          const response = await api(`/games/${gameId}/resign`, { method: 'PUT' }) // Reuse resign logic for delete
          setGames(prev => prev.filter(g => g.id !== gameId))
          return response
      }, false)
  }, [api, withState])

  const cancelChallenge = useCallback((gameId) => {
      return withState(async () => {
          const response = await api(`/games/${gameId}/challenge`, { method: 'DELETE' })
          setGames(prev => prev.filter(g => g.id !== gameId))
          setCurrentGame(null) // Clear current game if cancelled
          return response
      }, false)
  }, [api, withState])


  // --- Draw & Promotion ---
  const offerDraw = useCallback((gameId) => {
      return withState(async () => {
          const res = await api(`/games/${gameId}/draw/offer`, { method: 'POST' })
          if (currentGame?.id === gameId) setCurrentGame(res.game)
          return res
      }, false)
  }, [api, withState, currentGame?.id])

  const cancelDraw = useCallback((gameId) => {
      return withState(async () => {
          const res = await api(`/games/${gameId}/draw/offer`, { method: 'DELETE' })
          if (currentGame?.id === gameId) setCurrentGame(res.game)
          return res
      }, false)
  }, [api, withState, currentGame?.id])

  const respondDraw = useCallback((gameId, action) => {
      return withState(async () => {
          const res = await api(`/games/${gameId}/draw/respond`, { method: 'POST', body: { action } })
          if (currentGame?.id === gameId) setCurrentGame(res.game)
          return res
      }, false)
  }, [api, withState, currentGame?.id])

  const choosePromotion = useCallback((gameId, payload) => {
      return withState(async () => {
          return await api(`/games/${gameId}/promotion`, { method: 'POST', body: payload })
      }, false)
  }, [api, withState])

  // --- Profile & Social ---
  const getProfile = useCallback(() => withState(() => api('/profile'), false), [api, withState])
  
  const updateProfile = useCallback((updates) => {
      return withState(() => api('/profile', { method: 'PUT', body: updates }), false)
  }, [api, withState])

  const getMatchHistory = useCallback((limit = 50) => {
      return withState(async () => (await api(`/profile/match-history?limit=${limit}`)).matches || [], false)
  }, [api, withState])

  const getRatingHistory = useCallback((limit = 100) => {
      return withState(async () => (await api(`/profile/rating-history?limit=${limit}`)).rating_history || [], false)
  }, [api, withState])

  const getLeaderboard = useCallback((limit = 100) => {
      return withState(async () => (await api(`/leaderboard?limit=${limit}`)).leaderboard || [], false)
  }, [api, withState])

  const evaluateGame = useCallback((gameId) => {
      return withState(async () => (await api(`/games/${gameId}/evaluate`, { method: 'POST' })).game, false)
  }, [api, withState])

  const updateThemes = useCallback((pieceTheme, boardTheme) => {
      return withState(() => api('/profile/themes', { method: 'PUT', body: { piece_theme: pieceTheme, board_theme: boardTheme } }), false)
  }, [api, withState])

  const markReady = useCallback((gameId) => {
      return withState(async () => {
          const res = await api(`/games/${gameId}/ready`, { method: 'POST' })
          if (currentGame?.id === gameId) setCurrentGame(res)
          return res
      }, false)
  }, [api, withState, currentGame?.id])

  // --- Matchmaking Queue ---
  const joinQueue = useCallback((timeControl, rated) => {
      return withState(async () => {
          setLoading(true) // Explicit loading for queue
          const res = await api('/games/queue', { method: 'POST', body: { time_control: timeControl, rated } })
          setLoading(false) // Wait, original code kept it false? No, set to false.
          // Note: component usually handles "Searching..." UI
          return res
      })
  }, [api, withState])

  const leaveQueue = useCallback(async () => {
      try {
          await api('/games/queue', { method: 'DELETE' })
      } catch (err) {
          console.error("Failed to leave queue:", err)
      }
  }, [api])

  const checkQueueStatus = useCallback(async () => {
      // Don't trigger loading state for background check
      return withState(async () => {
          return await api('/games/queue')
      }, false)
  }, [api, withState])

  // Auto-refresh available games
  useEffect(() => {
    if (session?.access_token) {
      listAvailableGames()
    }
  }, [session?.access_token, listAvailableGames])

  const clearCurrentGame = useCallback(() => setCurrentGame(null), [])

  const value = useMemo(() => ({
    games,
    currentGame,
    availableGames,
    loading,
    error,
    createGame,
    joinGame,
    // makeMove, // Removed
    getGame,
    listGames,
    listAvailableGames,
    resignGame,
    deleteGame,
    cancelChallenge,
    choosePromotion,
    offerDraw,
    cancelDraw,
    respondDraw,
    clearCurrentGame,
    setCurrentGame,
    getProfile,
    updateProfile,
    getMatchHistory,
    getRatingHistory,
    getLeaderboard,
    evaluateGame,
    updateThemes,
    markReady,
    joinQueue,
    leaveQueue,
    checkQueueStatus
  }), [
    games, currentGame, availableGames, loading, error,
    createGame, joinGame, getGame, listGames, listAvailableGames,
    resignGame, deleteGame, choosePromotion,
    offerDraw, cancelDraw, respondDraw,
    clearCurrentGame, getProfile, updateProfile,
    getMatchHistory, getRatingHistory, getLeaderboard, evaluateGame,
    updateThemes, markReady, joinQueue, leaveQueue, checkQueueStatus
  ])

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  )
}
